import ReactECharts from 'echarts-for-react'
import { P360Card } from 'legacy/lut/components/widgets/public/partner360/p360Card'
import { FC, useMemo } from 'react'

import { Flex } from 'core-components/library/flex'
import { PARTNER360_CHART_FONT_FAMILY } from 'partner360/configs/typography'
import { TOUR_TARGETS } from 'partner360/configs/widgetTour/planningActivitiesTourTargets'
import { TKeyInitiative } from 'partner360/entities/keyInitiatives'
import { TRANSLATION_KEYS } from 'partner360/features/keyInitiatives/configs/translations'
import {
  getPlanQuarterField,
  getPrevYearActualsQuarterField,
} from 'partner360/features/keyInitiatives/lib/financialFieldHelper'
import { TKeyInitiativesFinancialData } from 'partner360/features/keyInitiatives/model/financialData'
import { DashboardChartMeasure } from 'partner360/features/keyInitiatives/model/types'
import {
  getGoalField,
  getInitiativeIncrementalField,
} from 'partner360/features/keyInitiatives/ui/helpers/fieldHelper'
import { getNumberValue } from 'partner360/features/keyInitiatives/ui/helpers/getNumberValue'
import { useKeyInitiativesControlsForm } from 'partner360/features/keyInitiatives/ui/hooks/useKeyInitiativesControlsForm'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { DashboardLabel } from '../../common/dashboardLabel/DashboardLabel'
import { QUARTERS } from '../configs'
import { formatChartValue } from '../helpers/formatChartValue'

import styles from './FinancialTargetCoverageChart.module.scss'

interface IFinancialTargetCoverageChartProps {
  kiData: TKeyInitiative[]
  finData: TKeyInitiativesFinancialData[]
  activeCurrency: string
  actualsType: ActualsValueTypes
}

type Quarter = (typeof QUARTERS)[number]

const getInitiativeQuarters = (period: string | null): Quarter[] => {
  if (!period) {
    return []
  }

  return period
    .split(',')
    .map((item) => item.trim().toUpperCase())
    .filter((item): item is Quarter => QUARTERS.includes(item as Quarter))
}

export const FinancialTargetCoverageChart: FC<IFinancialTargetCoverageChartProps> = ({
  kiData,
  finData,
  activeCurrency,
  actualsType,
}) => {
  const { chartMeasure } = useKeyInitiativesControlsForm()

  const formatValue = useMemo(() => formatChartValue(activeCurrency, true), [activeCurrency])

  const chartData = useMemo(() => {
    const measure =
      chartMeasure === DashboardChartMeasure.Count ? DashboardChartMeasure.Goal : chartMeasure

    const initiativeField =
      measure === DashboardChartMeasure.Goal
        ? getGoalField(actualsType, activeCurrency)
        : getInitiativeIncrementalField(actualsType, activeCurrency)

    const initiativeValuesByQuarter = QUARTERS.reduce<Record<Quarter, number>>(
      (acc, quarter) => {
        acc[quarter] = 0

        return acc
      },
      {} as Record<Quarter, number>
    )

    kiData.forEach((item) => {
      const quarters = getInitiativeQuarters(item.Period)

      if (!quarters.length) {
        return
      }

      const value = getNumberValue(item, initiativeField)

      const valuePerQuarter = value / quarters.length
      quarters.forEach((quarter) => {
        initiativeValuesByQuarter[quarter] += valuePerQuarter
      })
    })

    const finItem = finData[0]

    return QUARTERS.map((quarter) => {
      const value = initiativeValuesByQuarter[quarter]

      const currentYearPlan = getNumberValue(
        finItem,
        getPlanQuarterField(actualsType, activeCurrency, quarter)
      )

      const prevYearActuals = getNumberValue(
        finItem,
        getPrevYearActualsQuarterField(actualsType, activeCurrency, quarter)
      )

      const target =
        measure === DashboardChartMeasure.Goal ? currentYearPlan : currentYearPlan - prevYearActuals

      return {
        quarter,
        value,
        target,
        covered: Math.min(value, target),
        gapToTarget: Math.max(target - value, 0),
        overTarget: Math.max(value - target, 0),
      }
    })
  }, [kiData, finData, chartMeasure, actualsType, activeCurrency])

  const option = useMemo(
    () => ({
      textStyle: { fontFamily: PARTNER360_CHART_FONT_FAMILY },

      grid: {
        left: 70,
        right: 24,
        top: 95,
        bottom: 70,
        containLabel: true,
      },

      legend: {
        bottom: 0,
        left: 0,
        icon: 'circle',
        itemWidth: 10,
        itemHeight: 10,
        textStyle: {
          color: '#6b7280',
          fontSize: 14,
        },
      },

      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        valueFormatter: formatValue,
      },

      xAxis: {
        type: 'category',
        data: chartData.map((item) => item.quarter),
        axisTick: {
          show: false,
        },
        axisLabel: {
          color: '#666',
          fontSize: 14,
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: '#d9d9d9',
          },
        },
      },

      yAxis: {
        type: 'value',
        axisLabel: {
          color: '#666',
          fontSize: 14,
          formatter: formatValue,
        },
        splitLine: {
          lineStyle: {
            color: '#d9d9d9',
          },
        },
      },

      series: [
        {
          name: 'Covered',
          type: 'bar',
          stack: 'coverage',
          barWidth: '72%',
          data: chartData.map((item) => item.covered),
          itemStyle: {
            color: '#288bd6',
          },
        },
        {
          name: 'Gap to target',
          type: 'bar',
          stack: 'coverage',
          data: chartData.map((item) => item.gapToTarget),
          itemStyle: {
            color: '#e3e9f2',
          },
        },
        {
          name: 'Over target',
          type: 'bar',
          stack: 'coverage',
          data: chartData.map((item) => item.overTarget),
          itemStyle: {
            color: '#16a34a',
          },
        },
      ],
    }),
    [chartData, formatValue]
  )

  return (
    <P360Card className={styles.root}>
      <div data-tour-id={TOUR_TARGETS.analysisCoverageChart}>
        <Flex justifyContent={'space-between'} alignItems={'center'}>
          <DashboardLabel
            translationKey={TRANSLATION_KEYS.dashboard.charts.coverageBar.title}
            type={'title'}
            replaceParams={[
              { key: '{chartMeasure}', value: chartMeasure },
              { key: '{activeCurrency}', value: activeCurrency },
              { key: '{actualsType}', value: actualsType },
            ]}
          />
          <DashboardLabel
            translationKey={TRANSLATION_KEYS.dashboard.charts.coverageBar.subTitle}
            type={'subTitle'}
            replaceParams={[
              { key: '{chartMeasure}', value: chartMeasure },
              { key: '{activeCurrency}', value: activeCurrency },
              { key: '{actualsType}', value: actualsType },
            ]}
          />
        </Flex>
        <ReactECharts option={option} style={{ width: '100%', height: 460 }} />
      </div>
    </P360Card>
  )
}
